<template>
  <div class="dashboard-page">
    <Navbar />
    <div class="app-container">
      <Sidebar />

      <div id="main-content" class="main-content" tabindex="-1">
        <a
          id="accesskey-c"
          href="#accesskey-c"
          accesskey="C"
          class="accesskey-brick sr-only-focusable"
          title="主要內容區塊，快捷鍵：Alt+C"
          aria-label="主要內容區塊"
        >:::</a>
        <div v-if="loading" class="loading-container">
          <a-spin size="large" />
        </div>

        <div v-else-if="user" class="dashboard-content">
          <section class="dashboard-heading">
            <div class="dashboard-heading-top">
              <h1>{{ uiText.pageTitle }}</h1>
              <span class="dashboard-heading-badge"> 0 </span>
            </div>
          </section>

          <div class="section-title">
            <h2>{{ uiText.pageSubtitle }}</h2>
          </div>

          <section class="summary-section">
            <article
              v-for="card in summaryCards"
              :key="card.key"
              class="summary-card"
            >
              <div class="summary-card-content">
                <p class="summary-card-label">{{ card.label }}</p>
                <strong class="summary-card-value">{{ card.value }}</strong>
              </div>
            </article>
          </section>

          <div class="section-title">
            <h2>{{ uiText.trackingTitle }}</h2>
          </div>

          <section class="tracking-section">
            <div class="tracking-filters">
              <div class="search-field">
                <span class="search-input-wrap">
                  <span class="search-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="6 10 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M18.9167 21.6667H18.2583L18.025 21.4417C18.8417 20.4917 19.3333 19.2583 19.3333 17.9167C19.3333 14.925 16.9083 12.5 13.9167 12.5C10.925 12.5 8.5 14.925 8.5 17.9167C8.5 20.9083 10.925 23.3333 13.9167 23.3333C15.2583 23.3333 16.4917 22.8417 17.4417 22.025L17.6667 22.2583V22.9167L21.8333 27.075L23.075 25.8333L18.9167 21.6667ZM13.9167 21.6667C11.8417 21.6667 10.1667 19.9917 10.1667 17.9167C10.1667 15.8417 11.8417 14.1667 13.9167 14.1667C15.9917 14.1667 17.6667 15.8417 17.6667 17.9167C17.6667 19.9917 15.9917 21.6667 13.9167 21.6667Z"
                        fill="#696969"
                      />
                    </svg>
                  </span>
                  <input
                    v-model.trim="searchKeyword"
                    type="text"
                    :placeholder="uiText.searchPlaceholder"
                    :aria-label="uiText.searchPlaceholder || '關鍵字搜尋'"
                    :title="uiText.searchPlaceholder || '關鍵字搜尋'"
                  />
                </span>
              </div>

              <div class="filter-field">
                <div class="custom-select-wrap">
                  <span class="select-label">{{ uiText.statusFilter }}</span>
                  <span class="select-divider">|</span>
                  <select
                    v-model="selectedStatus"
                    class="select-input"
                    :aria-label="uiText.statusFilter || '狀態篩選'"
                    :title="uiText.statusFilter || '狀態篩選'"
                  >
                    <option value="all">{{ uiText.allStatus }}</option>
                    <option
                      v-for="option in statusOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <span class="select-arrow" aria-hidden="true">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="#1A1A1A" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
              </div>

              <div class="filter-field">
                <div class="custom-select-wrap">
                  <span class="select-label">{{ uiText.sortFilter }}</span>
                  <span class="select-divider">|</span>
                  <select
                    v-model="selectedSort"
                    class="select-input"
                    :aria-label="uiText.sortFilter || '排序方式'"
                    :title="uiText.sortFilter || '排序方式'"
                  >
                    <option value="latest">{{ uiText.sortLatest }}</option>
                    <option value="oldest">{{ uiText.sortOldest }}</option>
                    <option value="name">{{ uiText.sortName }}</option>
                  </select>
                  <span class="select-arrow" aria-hidden="true">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="#1A1A1A" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <div v-if="paginatedProposals.length" class="tracking-content">
              <div class="tracking-table-wrap">
                <table class="tracking-table">
                  <thead>
                    <tr>
                      <th>{{ uiText.columnDate }}</th>
                      <th>{{ uiText.columnName }}</th>
                      <th>{{ uiText.columnStatus }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="proposal in paginatedProposals"
                      :key="proposal.id"
                    >
                      <td>{{ proposal.submitDate }}</td>
                      <td class="proposal-name-cell">{{ proposal.name }}</td>
                      <td>
                        <span
                          class="status-chip"
                          :class="statusConfig[proposal.status].className"
                        >
                          <span class="status-dot"></span>
                          {{ statusConfig[proposal.status][statusLabelKey] }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="tracking-mobile-list">
                <article
                  v-for="proposal in paginatedProposals"
                  :key="`${proposal.id}-mobile`"
                  class="tracking-mobile-card"
                >
                  <div class="tracking-mobile-card-top">
                    <span class="mobile-card-date">{{
                      proposal.submitDate
                    }}</span>
                    <span
                      class="status-chip"
                      :class="statusConfig[proposal.status].className"
                    >
                      <span class="status-dot"></span>
                      {{ statusConfig[proposal.status][statusLabelKey] }}
                    </span>
                  </div>
                  <h3>{{ proposal.name }}</h3>
                </article>
              </div>

              <div class="pagination-wrap">
                <a-pagination
                  v-model:current="currentPage"
                  :page-size="pageSize"
                  :total="filteredProposals.length"
                  :show-size-changer="false"
                />
              </div>
            </div>

            <div v-else class="empty-state">
              <strong>{{ uiText.emptyTitle }}</strong>
              <template v-if="proposals.length > 0">
                <p>{{ uiText.emptyDescription }}</p>
                <button type="button" class="reset-button" @click="resetFilters">
                  {{ uiText.resetAction }}
                </button>
              </template>
            </div>
          </section>
        </div>

        <div v-else class="error-container">
          <p>{{ t("dashboard.userInfoError") }}</p>
          <a-button type="primary" @click="redirectToLogin">
            {{ t("dashboard.relogin") }}
          </a-button>
        </div>
      </div>
    </div>
    <FooterSection />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import Navbar from "../components/Navbar.vue";
import Sidebar from "../components/Sidebar.vue";
import FooterSection from "../components/FooterSection.vue";
import { authAPI } from "../services/api";

const router = useRouter();
const { t, locale } = useI18n();

const user = ref(null);
const loading = ref(true);
const isMobile = ref(window.innerWidth <= 768);
const searchKeyword = ref("");
const selectedStatus = ref("all");
const selectedSort = ref("latest");
const currentPage = ref(1);

const statusConfig = {
  underReview: {
    label: "提案審查中",
    labelEn: "Under Review",
    className: "status-under-review",
  },
  approved: {
    label: "提案通過",
    labelEn: "Approved",
    className: "status-approved",
  },
  pendingLaunch: {
    label: "待上架",
    labelEn: "Pending Launch",
    className: "status-pending-launch",
  },
  launched: {
    label: "已上架",
    labelEn: "Launched",
    className: "status-launched",
  },
  completed: {
    label: "提案完成",
    labelEn: "Completed",
    className: "status-completed",
  },
  rejected: {
    label: "提案不通過",
    labelEn: "Rejected",
    className: "status-rejected",
  },
};

const getTextByLocale = (zh, en) => (locale.value === "en-US" ? en : zh);

const uiText = computed(() => ({
  pageTitle: getTextByLocale("全部", "All"),
  pageSubtitle: getTextByLocale("提案總覽", "Proposal Overview"),
  trackingTitle: getTextByLocale("進度追蹤", "Progress Tracking"),
  searchLabel: getTextByLocale("搜尋", "Search"),
  searchPlaceholder: getTextByLocale("關鍵字", "Keyword"),
  statusFilter: getTextByLocale("狀態", "Status"),
  sortFilter: getTextByLocale("排序", "Sort"),
  allStatus: getTextByLocale("全部", "All"),
  sortLatest: getTextByLocale("上架時間（新 → 舊）", "Upload Time (New → Old)"),
  sortOldest: getTextByLocale("上架時間（舊 → 新）", "Upload Time (Old → New)"),
  sortName: getTextByLocale("提案名稱 A-Z", "Name A-Z"),
  columnDate: getTextByLocale("日期", "Date"),
  columnName: getTextByLocale("名稱", "Name"),
  columnStatus: getTextByLocale("狀態", "Status"),
  emptyTitle: proposals.value.length === 0
    ? getTextByLocale("暫無資料", "No data")
    : getTextByLocale("查無符合條件的提案", "No matching proposals"),
  emptyDescription: getTextByLocale(
    "請調整搜尋關鍵字或篩選條件後再試一次。",
    "Try adjusting the keyword or filters."
  ),
  resetAction: getTextByLocale("清除篩選", "Reset filters"),
  summaryTotal: getTextByLocale("提案總數", "Total Proposals"),
  summaryApproved: getTextByLocale("提案通過", "Approved"),
  summaryLaunched: getTextByLocale("已上架", "Launched"),
  summaryPending: getTextByLocale("待上架", "Pending Launch"),
}));

const statusLabelKey = computed(() =>
  locale.value === "en-US" ? "labelEn" : "label"
);

const buildMockProposals = () => [];

const proposals = ref(buildMockProposals());

const statusOptions = computed(() =>
  Object.entries(statusConfig).map(([value, config]) => ({
    value,
    label: config[statusLabelKey.value],
  }))
);

const summaryCards = computed(() => [
  {
    key: "total",
    label: uiText.value.summaryTotal,
    value: 0,
  },
  {
    key: "approved",
    label: uiText.value.summaryApproved,
    value: 0,
  },
  {
    key: "launched",
    label: uiText.value.summaryLaunched,
    value: 0,
  },
  {
    key: "pending",
    label: uiText.value.summaryPending,
    value: 0,
  },
]);

const filteredProposals = computed(() => {
  const keyword = searchKeyword.value.trim().toLowerCase();

  const list = proposals.value.filter((proposal) => {
    const matchesStatus =
      selectedStatus.value === "all" ||
      proposal.status === selectedStatus.value;
    const statusLabel = statusConfig[proposal.status][statusLabelKey.value];
    const matchesKeyword =
      !keyword ||
      proposal.name.toLowerCase().includes(keyword) ||
      proposal.submitDate.toLowerCase().includes(keyword) ||
      statusLabel.toLowerCase().includes(keyword);

    return matchesStatus && matchesKeyword;
  });

  const sortedList = [...list];

  if (selectedSort.value === "oldest") {
    sortedList.sort((a, b) => a.timestamp - b.timestamp);
  } else if (selectedSort.value === "name") {
    sortedList.sort((a, b) => a.name.localeCompare(b.name));
  } else {
    sortedList.sort((a, b) => b.timestamp - a.timestamp);
  }

  return sortedList;
});

const pageSize = computed(() => (isMobile.value ? 4 : 8));

const paginatedProposals = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return filteredProposals.value.slice(start, start + pageSize.value);
});

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

const redirectToLogin = () => {
  localStorage.removeItem("ms_platform_token");
  localStorage.removeItem("user_data");
  router.push("/sign-in");
};

const fetchUserInfo = async () => {
  try {
    const response = await authAPI.me();

    if (response.data) {
      user.value = response.data;
      localStorage.setItem("user_data", JSON.stringify(user.value));
      return;
    }

    user.value = null;
    message.error(t("dashboard.userInfoError"));
  } catch (error) {
    user.value = null;
    message.error(t("dashboard.userInfoError"));
  }
};

const checkAuth = () => {
  const token = localStorage.getItem("ms_platform_token");

  if (!token) {
    redirectToLogin();
    return false;
  }

  const storedUserData = localStorage.getItem("user_data");
  if (storedUserData) {
    try {
      user.value = JSON.parse(storedUserData);
    } catch (error) {
      localStorage.removeItem("user_data");
    }
  }

  return true;
};

const resetFilters = () => {
  searchKeyword.value = "";
  selectedStatus.value = "all";
  selectedSort.value = "latest";
  currentPage.value = 1;
};

watch([searchKeyword, selectedStatus, selectedSort, pageSize], () => {
  currentPage.value = 1;
});

watch(
  filteredProposals,
  (list) => {
    const maxPage = Math.max(1, Math.ceil(list.length / pageSize.value));
    if (currentPage.value > maxPage) {
      currentPage.value = maxPage;
    }
  },
  { immediate: true }
);

watch(locale, () => {
  proposals.value = buildMockProposals();
});

onMounted(async () => {
  window.addEventListener("resize", handleResize);

  if (!checkAuth()) {
    loading.value = false;
    return;
  }

  loading.value = true;
  try {
    await fetchUserInfo();
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  background: #f9f9f9;
  padding-top: 58px;
}

.app-container {
  display: flex;
  min-height: calc(100vh - 58px);
}

.main-content {
  flex: 1;
  height: calc(100vh - 58px);
  padding: 36px 16px 56px;
  overflow-y: auto;
}

.dashboard-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (max-width: 1650px) {
  .main-content {
    margin-left: 220px;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
}

.loading-container,
.error-container {
  min-height: calc(100vh - 170px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
}

.dashboard-heading {
  padding: 0 0 16px 0;
  margin-bottom: 32px;
  border-bottom: 2px solid #184387;
}

.dashboard-heading-top {
  display: flex;
  align-items: center;
  gap: 16px;
}

.dashboard-heading h1 {
  margin: 0;
  font-size: 32px;
  line-height: 1.2;
  color: #184387;
  font-weight: 700;
}

.dashboard-heading-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #184387;
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
}

.section-title {
  margin-bottom: 16px;
}

.section-title h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #184387;
}

.summary-section {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.summary-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 140px;
  padding: 24px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: none;
}

.summary-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.summary-card-label {
  margin: 0;
  font-size: 16px;
  color: #333333;
}

.summary-card-value {
  display: block;
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
  color: #184387;
  text-align: right;
  margin-top: 24px;
}

.tracking-section {
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

.tracking-filters {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) repeat(2, minmax(180px, 220px));
  gap: 16px;
  margin-bottom: 24px;
}

.search-field,
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  color: #667085;
}

.search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  box-sizing: border-box;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #696969;
  pointer-events: none;
}

.search-icon svg {
  width: 20px !important;
  height: 20px !important;
  display: block;
}

.search-field input {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  outline: none;
  padding: 0 12px 0 38px;
  font-size: 16px !important;
  font-family: inherit !important;
  color: #1a1a1a !important;
  box-sizing: border-box;
  &::placeholder {
    color: #696969;
  }
}

.custom-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  padding: 0 12px;
  box-sizing: border-box;

  .select-label {
    font-size: 16px !important;
    color: #1a1a1a;
    white-space: nowrap;
    user-select: none;
  }

  .select-divider {
    margin: 0 8px;
    color: #b4b4b4;
    font-size: 16px !important;
    user-select: none;
  }

  select.select-input {
    flex: 1;
    height: 100%;
    border: none;
    background: transparent !important;
    outline: none;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    cursor: pointer;
    padding-right: 20px;
    appearance: none;
    -webkit-appearance: none;

    option {
      color: #1a1a1a;
      background: #ffffff;
      font-size: 16px !important;
    }
  }

  .select-arrow {
    position: absolute;
    right: 12px;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
}

.tracking-table-wrap {
  overflow-x: auto;
  border: none;
  border-radius: 8px;
  background: #ffffff;
}

.tracking-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
}

.tracking-table thead {
  background: #0f2c5e;
}

.tracking-table th {
  padding: 16px 20px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
}

.tracking-table th:first-child {
  border-top-left-radius: 8px;
}

.tracking-table th:last-child {
  border-top-right-radius: 8px;
}

.tracking-table td {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
  color: #333333;
  vertical-align: middle;
}

.tracking-table tbody tr:last-child td {
  border-bottom: none;
}

.proposal-name-cell {
  font-weight: 600;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #333333;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-under-review .status-dot {
  background: #1677ff;
}

.status-pending-launch .status-dot {
  background: #fadb14;
}

.status-launched .status-dot {
  background: #13c2c2;
}

.status-completed .status-dot {
  background: #52c41a;
}

.status-rejected .status-dot {
  background: #ff4d4f;
}

/* Also for approved status if any */
.status-approved .status-dot {
  background: #52c41a;
}

.tracking-mobile-list {
  display: none;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.empty-state {
  min-height: 260px;
  border: 1px dashed #c9d7e8;
  border-radius: 18px;
  background: #f8fbff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 32px 20px;
}

.empty-state strong {
  font-size: 18px;
  color: #333333;
}

.empty-state p {
  margin: 0;
  color: #667085;
}

.reset-button {
  border: none;
  border-radius: 999px;
  background: #1171DD;
  color: #ffffff;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
}

.reset-button:hover {
  background: #00669d;
}

:deep(.ant-pagination-item-active) {
  border-color: #1171DD;
}

:deep(.ant-pagination-item-active a) {
  color: #1171DD;
}

@media (max-width: 1100px) {
  .tracking-filters {
    grid-template-columns: 1fr 1fr;
  }

  .search-field {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 24px 16px 36px;
  }

  .dashboard-content {
    gap: 20px;
  }

  .dashboard-heading {
    padding: 0 0 16px 0;
    border-radius: 0;
  }

  .tracking-section {
    padding: 20px 0;
    border-radius: 0;
  }

  .dashboard-heading-top {
    align-items: flex-start;
  }

  .dashboard-heading h1 {
    font-size: 28px;
  }

  .summary-section {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .summary-card {
    min-height: auto;
    padding: 12px 8px;
  }

  .summary-card-label {
    font-size: 13px;
  }

  .summary-card-value {
    font-size: 24px;
    margin-top: 12px;
  }

  .tracking-filters {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .search-field {
    grid-column: 1 / -1;
  }

  .tracking-table-wrap {
    display: none;
  }

  .tracking-mobile-list {
    display: grid;
    gap: 14px;
  }

  .tracking-mobile-card {
    border: 1px solid #e4edf7;
    border-radius: 18px;
    padding: 16px;
    background: #ffffff;
  }

  .tracking-mobile-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .mobile-card-date {
    font-size: 13px;
    color: #667085;
  }

  .tracking-mobile-card h3 {
    margin: 0 0 12px;
    font-size: 16px;
    line-height: 1.5;
    color: #333333;
  }

  .tracking-mobile-card-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: #667085;
  }

  .pagination-wrap {
    justify-content: center;
  }
}
</style>
